/**
 * BulkUploadResultsDialog Component
 * Dialog showing results after bulk certificate upload completes
 */

import { memo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CheckCircle, X, AlertTriangle } from 'lucide-react';

const BulkUploadResultsDialog = memo(({
  open,
  data,
  onClose
}) => {
  if (!data) return null;

  const successCount = data.successes?.length || 0;
  const failureCount = data.failures?.length || 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {successCount > 0 ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            )}
            Bulk Upload Complete
          </DialogTitle>
          <DialogDescription>
            {successCount} certificates matched and saved
            {failureCount > 0 && `, ${failureCount} failed`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Successes */}
          {successCount > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Successfully Saved ({successCount})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {data.successes.map((item, idx) => (
                  <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-green-900">{item.memberName}</div>
                        <div className="text-green-700">{item.requirementName}</div>
                        <div className="text-xs text-green-600 truncate">{item.fileName}</div>
                      </div>
                      {item.isNewAssignment && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full flex-shrink-0">
                          Assigned
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Failures */}
          {failureCount > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                <X className="h-4 w-4" />
                Failed ({failureCount})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {data.failures.map((item, idx) => (
                  <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                    <div className="font-medium text-red-900 truncate">{item.fileName}</div>
                    <div className="text-red-700">{item.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

BulkUploadResultsDialog.displayName = 'BulkUploadResultsDialog';

export default BulkUploadResultsDialog;
